import { useEffect } from "react";
import Tooltip from '@mui/material/Tooltip';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import { useShallow } from "zustand/react/shallow";

import { getRecipeRating, setRecipeRating } from "~/providers/api";
import type { IRecipeRating } from "~/providers/interfaces";
import { userStore } from "~/stores/user";
import { notificationStore } from '~/stores/notification';
import { trans } from "~/utils/trans";
import { classNames } from "~/utils/utils";
import { myUseState } from "~/hooks/myUseState";

import "./style.less";

interface IState {
	rating: IRecipeRating;
	saving: boolean;
}

interface IRecipeRatingProps {
	recipeId: number;
	// jen pocty bez moznosti hlasovat (nahled receptu v administraci)
	readOnly?: boolean;
}

// like / dislike receptu s pocty; z like na dislike (a naopak) se jde jen pres zruseni hodnoceni
export default function RecipeRating({
	recipeId,
	readOnly = false,
} : IRecipeRatingProps) {
	const { userData } = userStore(useShallow(userState => ({
		userData: userState.user,
	})));
	const setNotification = notificationStore.getState().setNotification;
	const { state, updateState } = myUseState<IState>({
		rating: null,
		saving: false,
	});

	async function load() {
		const ratingData = await getRecipeRating(userData.jwt, recipeId);

		if (ratingData.noErrors) {
			updateState({ rating: ratingData.data });
		}
	}

	async function rate(value: number) {
		if (state.saving) {
			return;
		}

		// opetovne kliknuti na zvolene hodnoceni ho zrusi
		const newValue = state.rating.my_rating === value ? 0 : value;

		updateState({ saving: true });

		const ratingData = await setRecipeRating(userData.jwt, recipeId, newValue);

		if (ratingData.noErrors) {
			updateState({ rating: ratingData.data, saving: false });
		} else {
			updateState({ saving: false });
			setNotification(ratingData.errors.join(", "), "error");
		}
	}

	useEffect(() => {
		if (userData.jwt && recipeId) {
			load();
		}
	}, [userData.jwt, recipeId]);

	if (!state.rating) {
		return null;
	}

	const my = state.rating.my_rating;

	function renderButton(value: number, count: number) {
		const isLike = value === 1;
		const active = my === value;
		// s opacnym hodnocenim jde jen zrusit
		const blocked = my === -value;
		const icon = isLike
			? (active ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />)
			: (active ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />);
		let title = "";

		if (!readOnly) {
			if (blocked) {
				title = trans(isLike ? "recipeRating.removeDislikeFirst" : "recipeRating.removeLikeFirst");
			} else if (active) {
				title = trans("recipeRating.remove");
			} else {
				title = trans(isLike ? "recipeRating.like" : "recipeRating.dislike");
			}
		}

		const button = <button
			type="button"
			className={classNames(["recipeRating__button", isLike ? "recipeRating__button--like" : "recipeRating__button--dislike", active ? "recipeRating__button--active" : ""])}
			disabled={readOnly || blocked || state.saving}
			aria-pressed={active}
			aria-label={title || trans(isLike ? "recipeRating.likes" : "recipeRating.dislikes")}
			onClick={() => rate(value)}
		>
			{icon}
			<span>{count}</span>
		</button>;

		// tooltip i na neaktivnim tlacitku - neaktivni prvek udalosti mysi nevyvolava, proto obal
		return title
			? <Tooltip title={title}><span>{button}</span></Tooltip>
			: button;
	}

	return <div className={classNames(["recipeRating", readOnly ? "recipeRating--readOnly" : ""])}>
		{ renderButton(1, state.rating.likes) }
		{ renderButton(-1, state.rating.dislikes) }
	</div>;
}
